import { useEffect, useRef } from "react";
import type { BehaviorDump } from "../api/types";

interface Line { label: string; color: string; data: number[] }
interface Panel { title: string; lines: Line[]; fixedY?: [number, number]; kind?: "motors"; motors?: number[][] }

// Faithful port of the Evo Console's drawBehavior(): per-objective stacked panels
// of the best organism's trajectory (altitude / horizontal dist / speed / gyro).
function buildPanels(b: BehaviorDump): { panels: Panel[]; ts: number[] } {
  const log = b.log;
  if (b.panels && Array.isArray(b.panels) && (b.panels as Panel[]).length && b.t && b.t.length) {
    return { panels: b.panels as Panel[], ts: b.t };
  }
  const ts = log.map((e) => e.t);
  const hasGyro = log[0].gyro !== undefined;
  const S: Record<string, { d: number[] | null; c: string; l: string }> = {
    altitude: { d: log.map((e) => -e.pos[2]), c: "#3dba6e", l: "altitude (m)" },
    horiz: { d: log.map((e) => Math.hypot(e.pos[0], e.pos[1])), c: "#4a9eff", l: "‖xy‖ (m)" },
    speed: { d: log.map((e) => Math.hypot(e.vel[0], e.vel[1], e.vel[2])), c: "#e8a020", l: "‖v‖ (m/s)" },
    yaw_rate: { d: hasGyro ? log.map((e) => e.gyro[2]) : null, c: "#e84040", l: "gyro_z (rad/s)" },
    gyro_xy: { d: hasGyro ? log.map((e) => Math.hypot(e.gyro[0], e.gyro[1])) : null, c: "#e8a020", l: "‖gyro_xy‖" },
    gyro_mag: { d: hasGyro ? log.map((e) => Math.hypot(e.gyro[0], e.gyro[1], e.gyro[2])) : null, c: "#e84040", l: "‖gyro‖" },
  };
  const FT: Record<string, string[]> = {
    hover_mjx_pos_plus_yaw: ["altitude", "horiz", "yaw_rate"],
    hover_mjx_pos_plus_gyro: ["altitude", "horiz", "gyro_mag"],
    hover_mjx_yaw_primary: ["altitude", "yaw_rate"],
    hover_mjx_drift_3term: ["horiz", "yaw_rate", "gyro_xy"],
    // control prior: altitude, lateral drift, and the FULL spin rate (what it minimizes)
    stableflight: ["altitude", "horiz", "gyro_mag"],
  };
  let names = (FT[b.fitness_type] || ["altitude", "horiz", "speed"]).filter((n) => S[n] && S[n].d);
  if (!names.length) names = ["altitude"];
  const panels: Panel[] = names.map((n) => ({ title: S[n].l, lines: [{ label: S[n].l, color: S[n].c, data: S[n].d! }] }));
  // Motor-command panel (rendered by drawMotorPanel below, not as generic lines). The log's
  // t=0 spawn row carries no cmd — anchor it to the FIRST real command so the trace doesn't
  // fake a jump up from zero. Four rotor commands, tanh [-1,1].
  const firstCmd = log.find((e) => e.cmd && e.cmd.length >= 4)?.cmd;
  if (firstCmd) {
    const motors = [0, 1, 2, 3].map((k) =>
      log.map((e) => (e.cmd && e.cmd.length >= 4 ? e.cmd[k] : firstCmd[k])));
    panels.push({ title: "motors", kind: "motors", motors, lines: [] });
  }
  return { panels, ts };
}

// Motor-command panel: the 4 rotor commands (tanh [-1,1]) as a control-meaningful decomposition
// instead of 4 coincident lines. COLLECTIVE (mean, bright line) = throttle / altitude authority;
// the shaded MIN–MAX BAND = the attitude differential (how far the rotors split — thin band ⇒ pure
// collective, wide ⇒ hard attitude action); faint per-rotor lines let you trace one motor; the ±1
// edges are shaded so saturation is unmistakable. Fixed [-1,1] axis ⇒ the level is absolute.
function drawMotorPanel(
  ctx: CanvasRenderingContext2D, motors: number[][], ts: number[],
  sx: (t: number) => number, top: number, bottom: number, xL: number, xR: number,
) {
  const M = motors.length;
  const N = Math.min(motors[0]?.length ?? 0, ts.length);
  if (M < 1 || N < 2) return;
  const h = bottom - top, midY = top + h / 2;
  const sy = (v: number) => midY - Math.max(-1, Math.min(1, v)) * (h / 2 - 1);
  const MC = ["#e84040", "#3dba6e", "#4a9eff", "#e8a020"];

  // frame + saturation zones (|cmd| > 0.9) + zero baseline
  ctx.strokeStyle = "#1e1e1e"; ctx.lineWidth = 1; ctx.strokeRect(xL, top, xR - xL, h);
  ctx.fillStyle = "rgba(232,64,64,0.07)";
  ctx.fillRect(xL, sy(1), xR - xL, sy(0.9) - sy(1));
  ctx.fillRect(xL, sy(-0.9), xR - xL, sy(-1) - sy(-0.9));
  ctx.strokeStyle = "#2b2b2b"; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(xL, midY); ctx.lineTo(xR, midY); ctx.stroke();

  // per-tick collective (mean) and spread (min..max) across the rotors
  const mean = new Array<number>(N), lo = new Array<number>(N), hi = new Array<number>(N);
  for (let i = 0; i < N; i++) {
    let s = 0, mn = Infinity, mx = -Infinity;
    for (let m = 0; m < M; m++) { const v = motors[m][i]; s += v; if (v < mn) mn = v; if (v > mx) mx = v; }
    mean[i] = s / M; lo[i] = mn; hi[i] = mx;
  }

  // spread band (min..max envelope)
  ctx.fillStyle = "rgba(120,150,200,0.20)"; ctx.beginPath();
  for (let i = 0; i < N; i++) { const x = sx(ts[i]), y = sy(hi[i]); if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); }
  for (let i = N - 1; i >= 0; i--) ctx.lineTo(sx(ts[i]), sy(lo[i]));
  ctx.closePath(); ctx.fill();

  // faint individual rotors
  ctx.globalAlpha = 0.55; ctx.lineWidth = 0.8;
  for (let m = 0; m < M; m++) {
    ctx.strokeStyle = MC[m % MC.length]; ctx.beginPath();
    for (let i = 0; i < N; i++) { const x = sx(ts[i]), y = sy(motors[m][i]); if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // collective (mean) — the headline throttle
  ctx.strokeStyle = "#eaeaea"; ctx.lineWidth = 1.8; ctx.beginPath();
  for (let i = 0; i < N; i++) { const x = sx(ts[i]), y = sy(mean[i]); if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); }
  ctx.stroke();

  // axis labels + readout
  ctx.font = '9px ui-monospace, monospace';
  ctx.fillStyle = "#666"; ctx.textAlign = "right";
  ctx.fillText("+1", xL - 3, top + 8); ctx.fillText("0", xL - 3, midY + 3); ctx.fillText("−1", xL - 3, bottom - 1);
  ctx.fillStyle = "#8a93a0"; ctx.textAlign = "left";
  ctx.fillText("motors  — collective   ▒ spread", xL + 4, top + 10);
  let satN = 0; for (let m = 0; m < M; m++) for (let i = 0; i < N; i++) if (Math.abs(motors[m][i]) > 0.98) satN++;
  let maxSpread = 0; for (let i = 0; i < N; i++) if (hi[i] - lo[i] > maxSpread) maxSpread = hi[i] - lo[i];
  ctx.fillStyle = "#9aa4b0"; ctx.textAlign = "right";
  ctx.fillText(`coll ${mean[N - 1].toFixed(2)}  spread ${maxSpread.toFixed(2)}  sat ${((satN / (M * N)) * 100).toFixed(0)}%`, xR - 4, top + 10);
}

function draw(canvas: HTMLCanvasElement, b: BehaviorDump) {
  const wrap = canvas.parentElement!;
  const W = wrap.clientWidth, H = wrap.clientHeight;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, W, H);
  const { panels, ts } = buildPanels(b);
  if (!panels.length || ts.length < 2) return;

  const PAD_L = 40, PAD_R = 8, PAD_T = 4, PAD_B = 16, GAP = 8;
  const tMin = ts[0], tMax = ts[ts.length - 1], tRange = tMax - tMin || 1;
  // Reserve GAP between stacked panels so they breathe; panels share the remainder.
  const subH = (H - PAD_T - PAD_B - GAP * (panels.length - 1)) / panels.length;
  const sx = (t: number) => PAD_L + ((t - tMin) / tRange) * (W - PAD_L - PAD_R);
  ctx.font = '9px ui-monospace, monospace';

  for (let k = 0; k < panels.length; k++) {
    if (panels[k].kind === "motors" && panels[k].motors) {
      const mtop = PAD_T + k * (subH + GAP);
      drawMotorPanel(ctx, panels[k].motors!, ts, sx, mtop, mtop + subH, PAD_L, W - PAD_R);
      continue;
    }
    const lines = panels[k].lines.filter((l) => l.data && l.data.length);
    if (!lines.length) continue;
    let yMin: number, yMax: number;
    if (panels[k].fixedY) {
      // Bounded signal (e.g. motors [-1,1]): pin the axis so the level is absolute,
      // not relative — a flat hover stays flat and saturation is visible.
      [yMin, yMax] = panels[k].fixedY!;
    } else {
      yMin = Infinity; yMax = -Infinity;
      for (const ln of lines) for (const v of ln.data) if (Number.isFinite(v)) { if (v < yMin) yMin = v; if (v > yMax) yMax = v; }
      if (!(yMax > yMin)) yMax = yMin + 1;
      // Floor the span so a near-zero/pinned trace isn't auto-magnified into fake
      // oscillation with "0.00 / 0.00" labels — show it flat near its value instead.
      const MIN_SPAN = 0.1;
      if (yMax - yMin < MIN_SPAN) { const c = (yMin + yMax) / 2; yMin = c - MIN_SPAN / 2; yMax = c + MIN_SPAN / 2; }
    }
    const yRange = yMax - yMin;
    const top = PAD_T + k * (subH + GAP), bottom = top + subH;
    const sy = (y: number) => top + (1 - (Math.max(yMin, Math.min(yMax, y)) - yMin) / yRange) * (bottom - top);
    ctx.strokeStyle = "#1e1e1e";
    ctx.lineWidth = 1;
    ctx.strokeRect(PAD_L, top, W - PAD_L - PAD_R, bottom - top);
    ctx.fillStyle = "#8a93a0";
    ctx.textAlign = "left";
    ctx.fillText(panels[k].title, PAD_L + 4, top + 10);
    ctx.fillStyle = "#666";
    ctx.textAlign = "right";
    ctx.fillText(yMax.toFixed(2), PAD_L - 3, top + 8);
    ctx.fillText(yMin.toFixed(2), PAD_L - 3, bottom - 1);
    let lx = PAD_L + 6 + ctx.measureText(panels[k].title).width + 10;
    ctx.textAlign = "left";
    for (const ln of lines) {
      ctx.strokeStyle = ln.color;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      const n = Math.min(ln.data.length, ts.length);
      for (let i = 0; i < n; i++) {
        const x = sx(ts[i]), y = sy(ln.data[i]);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
      if (lines.length > 1) {
        ctx.fillStyle = ln.color;
        ctx.fillText(ln.label, lx, top + 10);
        lx += ctx.measureText(ln.label).width + 10;
      }
    }
  }
  ctx.fillStyle = "#666";
  ctx.textAlign = "center";
  for (let i = 0; i <= 4; i++) {
    const tv = tMin + (tRange * i) / 4;
    ctx.fillText(`${tv.toFixed(1)}s`, sx(tv), H - 2);
  }
}

export function BehaviorCanvas({ behavior }: { behavior: BehaviorDump | null }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c || !behavior) return;
    const render = () => draw(c, behavior);
    render();
    const ro = new ResizeObserver(render);
    ro.observe(c.parentElement!);
    return () => ro.disconnect();
  }, [behavior]);
  const ok = behavior && behavior.log && behavior.log.length >= 2;
  return (
    <div className="viz-wrap">
      {!ok && <div className="viz-empty">no behavior yet (written on each new best)</div>}
      <canvas ref={ref} />
    </div>
  );
}
